import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { getUserAuth } from "../../recoil/selectors";
import { GetPatientReviewRequest } from "./Services/DetailedResultsService";

interface DelayedPatientReviewModalData {
  setShowPatientReviewModal: (show: boolean) => void;
  showPatientReviewModal: boolean;
}

/**
 * Shows the patient review modal `delayMs` after `enabled` becomes true,
 * unless the patient has already left a review.
 */
export const useDelayedPatientReviewModal = (
  delayMs: number,
  enabled = true,
): DelayedPatientReviewModalData => {
  const auth = useRecoilValue(getUserAuth);
  const [showPatientReviewModal, setShowPatientReviewModal] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setShowPatientReviewModal(false);
      return undefined;
    }

    let timer: number | undefined;
    let isMounted = true;

    async function getPatientReview(): Promise<void> {
      try {
        const response = await GetPatientReviewRequest(auth?.access_token);
        if (!response.ok) {
          console.error("Patient review lookup failed", {
            responseStatus: response.status,
          });
          return;
        }
        const patientReviews: unknown = await response.json();
        if (
          Array.isArray(patientReviews) &&
          patientReviews.length === 0 &&
          isMounted
        ) {
          timer = window.setTimeout(() => {
            if (isMounted) {
              setShowPatientReviewModal(true);
            }
          }, delayMs);
        }
      } catch (error) {
        console.error("Patient review lookup failed", error);
        // A failed review lookup must not prompt the patient as though the
        // service confirmed that no review exists.
      }
    }

    getPatientReview();

    return () => {
      isMounted = false;
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, [auth?.access_token, delayMs, enabled]);

  return { setShowPatientReviewModal, showPatientReviewModal };
};
